import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { AppLayout } from '../components/AppLayout';
import { TimeRangeFields } from '../components/TimeRangeFields';
import { createBooking } from '../services/bookings';
import { notifyNewBookingRequest } from '../services/notifyBooking';
import {
  BRANCA_LABELS,
  BRANCHE,
  Branca,
  isBranca,
  isValidEmail,
  isValidPhone,
  normalizePhone,
  REGOLAMENTO_PDF_PATH,
  sanitizePhoneInput,
} from '../types/booking';
import { isValidRange } from '../utils/slots';
import { colors } from '../theme';

interface FormState {
  gruppoScout: string;
  branca: Branca | '';
  numeroRagazzi: string;
  numeroAdulti: string;
  referenteNome: string;
  telefono: string;
  email: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  altreInfo: string;
}

type ToastState = {
  severity: 'success' | 'error';
  message: string;
} | null;

const today = () => dayjs().format('YYYY-MM-DD');

function createInitialForm(): FormState {
  return {
    gruppoScout: '',
    branca: '',
    numeroRagazzi: '',
    numeroAdulti: '',
    referenteNome: '',
    telefono: '',
    email: '',
    startDate: today(),
    endDate: today(),
    startTime: '09:00',
    endTime: '11:00',
    altreInfo: '',
  };
}

function isFormDirty(form: FormState, initial: FormState): boolean {
  return (
    form.gruppoScout.trim() !== initial.gruppoScout ||
    form.branca !== initial.branca ||
    form.numeroRagazzi.trim() !== initial.numeroRagazzi ||
    form.numeroAdulti.trim() !== initial.numeroAdulti ||
    form.referenteNome.trim() !== initial.referenteNome ||
    form.telefono.trim() !== initial.telefono ||
    form.email.trim() !== initial.email ||
    form.startDate !== initial.startDate ||
    form.endDate !== initial.endDate ||
    form.startTime !== initial.startTime ||
    form.endTime !== initial.endTime ||
    form.altreInfo.trim() !== initial.altreInfo
  );
}

export function BookingPage() {
  const navigate = useNavigate();
  const [initialForm] = useState(createInitialForm);
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);
  const [success, setSuccess] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [regolamentoAccepted, setRegolamentoAccepted] = useState(false);

  const sameDay = form.startDate === form.endDate;
  const dirty = isFormDirty(form, initialForm);

  const rangePreview = useMemo(() => {
    if (!form.startDate || !form.endDate || !form.startTime || !form.endTime) {
      return null;
    }
    const payload = {
      startDate: form.startDate,
      endDate: form.endDate,
      startTime: form.startTime,
      endTime: form.endTime,
    };
    if (!isValidRange(payload)) return null;
    return payload;
  }, [form.startDate, form.endDate, form.startTime, form.endTime]);

  const submitBlockers = useMemo(() => {
    const issues: string[] = [];
    const ragazzi = Number(form.numeroRagazzi);
    const adulti = Number(form.numeroAdulti);

    if (form.gruppoScout.trim() === '') issues.push('Gruppo scout');
    if (!isBranca(form.branca)) issues.push('Branca');
    if (
      form.numeroRagazzi.trim() === '' ||
      !Number.isFinite(ragazzi) ||
      ragazzi < 0
    ) {
      issues.push('Ragazzi previsti (anche 0)');
    }
    if (
      form.numeroAdulti.trim() === '' ||
      !Number.isFinite(adulti) ||
      adulti < 1
    ) {
      issues.push('Adulti previsti (almeno 1)');
    }
    if (form.referenteNome.trim() === '') {
      issues.push('Nome e cognome referente');
    }
    if (!isValidPhone(form.telefono)) issues.push('Telefono valido');
    if (!isValidEmail(form.email.trim())) issues.push('Email valida');
    if (
      !form.startDate ||
      !form.endDate ||
      !form.startTime ||
      !form.endTime ||
      dayjs(form.endDate).isBefore(dayjs(form.startDate), 'day') ||
      rangePreview === null
    ) {
      issues.push('Date e orari validi');
    }
    if (!regolamentoAccepted) issues.push('Accettazione del regolamento');

    return issues;
  }, [form, rangePreview, regolamentoAccepted]);

  const canSubmit = submitBlockers.length === 0;

  const goBack = () => navigate('/');

  const handleBackClick = () => {
    if (dirty && !success) {
      setConfirmOpen(true);
      return;
    }
    goBack();
  };

  const update =
    (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const raw = e.target.value;
      const value = field === 'telefono' ? sanitizePhoneInput(raw) : raw;
      setForm((prev) => ({ ...prev, [field]: value }));
      setSuccess(false);
      setToast(null);
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setToast(null);
    setSuccess(false);

    const numero = Number(form.numeroRagazzi);
    const adulti = Number(form.numeroAdulti);
    if (!form.gruppoScout.trim()) {
      setToast({ severity: 'error', message: 'Inserisci il gruppo scout.' });
      return;
    }
    if (!isBranca(form.branca)) {
      setToast({
        severity: 'error',
        message: 'Seleziona per che branca prenoti.',
      });
      return;
    }
    if (form.numeroRagazzi.trim() === '' || !Number.isFinite(numero) || numero < 0) {
      setToast({
        severity: 'error',
        message: 'Indica il numero di ragazzi previsti (anche 0).',
      });
      return;
    }
    if (form.numeroAdulti.trim() === '' || !Number.isFinite(adulti) || adulti < 1) {
      setToast({
        severity: 'error',
        message: 'Indica un numero di adulti valido (almeno 1).',
      });
      return;
    }
    if (!form.referenteNome.trim()) {
      setToast({
        severity: 'error',
        message: 'Inserisci nome e cognome del referente.',
      });
      return;
    }
    if (!isValidPhone(form.telefono)) {
      setToast({
        severity: 'error',
        message: 'Inserisci un numero di cellulare valido.',
      });
      return;
    }
    if (!isValidEmail(form.email.trim())) {
      setToast({
        severity: 'error',
        message: 'Inserisci un’email valida.',
      });
      return;
    }
    if (!form.startDate || !form.endDate) {
      setToast({
        severity: 'error',
        message: 'Seleziona data di inizio e fine.',
      });
      return;
    }
    if (dayjs(form.endDate).isBefore(dayjs(form.startDate), 'day')) {
      setToast({
        severity: 'error',
        message: 'La data di fine non può essere prima della data di inizio.',
      });
      return;
    }
    if (!form.startTime || !form.endTime) {
      setToast({
        severity: 'error',
        message: 'Seleziona ora di inizio e fine.',
      });
      return;
    }
    if (!regolamentoAccepted) {
      setToast({
        severity: 'error',
        message: 'Devi accettare il Regolamento della base per inviare.',
      });
      return;
    }

    const payload = {
      gruppoScout: form.gruppoScout.trim(),
      branca: form.branca,
      numeroRagazzi: numero,
      numeroAdulti: adulti,
      referenteNome: form.referenteNome.trim(),
      telefono: normalizePhone(form.telefono),
      email: form.email.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
      startTime: form.startTime,
      endTime: form.endTime,
      altreInfo: form.altreInfo.trim(),
    };

    if (!isValidRange(payload)) {
      setToast({
        severity: 'error',
        message: 'L’orario di fine deve essere successivo all’inizio.',
      });
      return;
    }

    setSubmitting(true);
    try {
      await createBooking(payload);
      void notifyNewBookingRequest(payload);
      setSuccess(true);
      setForm(createInitialForm());
      setRegolamentoAccepted(false);
      setToast({
        severity: 'success',
        message:
          'Prenotazione inviata! Apparirà in calendario dopo l’approvazione.',
      });
    } catch (err) {
      setToast({
        severity: 'error',
        message:
          err instanceof Error
            ? err.message
            : 'Errore durante l’invio della prenotazione.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout
      title="Nuova prenotazione"
      hideAdmin
      maxWidth="lg"
      leading={
        <IconButton
          color="inherit"
          edge="start"
          aria-label="Torna indietro"
          onClick={handleBackClick}
          size="large"
        >
          <ArrowBackIcon />
        </IconButton>
      }
    >
      <Stack spacing={{ xs: 2, sm: 3 }}>
        <Box sx={{ px: { xs: 0.5, sm: 0 } }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: colors.primary,
              fontSize: { xs: '1.5rem', sm: '2rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Prenota la sede
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mt: 1, maxWidth: 520, lineHeight: 1.5 }}
          >
            Scegli periodo e orari (ogni 30 minuti). La richiesta deve essere approvata.
          </Typography>
        </Box>

        <Card>
          <CardContent
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              '&:last-child': { pb: { xs: 2.5, sm: 3.5 } },
            }}
          >
            <Stack
              component="form"
              spacing={2.75}
              onSubmit={handleSubmit}
              noValidate
            >
              <TextField
                label="Gruppo"
                value={form.gruppoScout}
                onChange={update('gruppoScout')}
                required
                fullWidth
                autoComplete="organization"
              />

              <TextField
                select
                label="Per che branca prenoti"
                value={form.branca}
                onChange={update('branca')}
                required
                fullWidth
              >
                {BRANCHE.map((b) => (
                  <MenuItem key={b} value={b}>
                    {BRANCA_LABELS[b]}
                  </MenuItem>
                ))}
              </TextField>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Ragazzi previsti"
                  type="number"
                  value={form.numeroRagazzi}
                  onChange={update('numeroRagazzi')}
                  required
                  fullWidth
                  slotProps={{ htmlInput: { min: 0, step: 1 } }}
                />
                <TextField
                  label="Adulti previsti"
                  type="number"
                  value={form.numeroAdulti}
                  onChange={update('numeroAdulti')}
                  required
                  fullWidth
                  slotProps={{ htmlInput: { min: 1, step: 1 } }}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Nome e cognome referente"
                  value={form.referenteNome}
                  onChange={update('referenteNome')}
                  required
                  fullWidth
                  autoComplete="name"
                />
                <TextField
                  label="Numero di telefono"
                  type="tel"
                  value={form.telefono}
                  onChange={update('telefono')}
                  required
                  fullWidth
                  autoComplete="tel"
                  placeholder="es. 333 1234567 oppure +39…"
                  helperText="Accetta anche prefisso internazionale (+39…)"
                />
              </Stack>

              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={update('email')}
                required
                fullWidth
                autoComplete="email"
                placeholder="per ricevere conferma o annullamento"
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Data inizio"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => {
                    const startDate = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      startDate,
                      endDate:
                        prev.endDate < startDate ? startDate : prev.endDate,
                    }));
                    setSuccess(false);
                    setToast(null);
                  }}
                  required
                  fullWidth
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { min: today() },
                  }}
                />
                <TextField
                  label="Data fine"
                  type="date"
                  value={form.endDate}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, endDate: e.target.value }));
                    setSuccess(false);
                    setToast(null);
                  }}
                  required
                  fullWidth
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { min: form.startDate || today() },
                  }}
                />
              </Stack>

              <TimeRangeFields
                startTime={form.startTime}
                endTime={form.endTime}
                sameDay={sameDay}
                onStartChange={(startTime) => {
                  setForm((prev) => {
                    const next = { ...prev, startTime };
                    if (
                      prev.startDate === prev.endDate &&
                      prev.endTime &&
                      prev.endTime <= startTime
                    ) {
                      next.endTime = '';
                    }
                    return next;
                  });
                  setSuccess(false);
                  setToast(null);
                }}
                onEndChange={(endTime) => {
                  setForm((prev) => ({ ...prev, endTime }));
                  setSuccess(false);
                  setToast(null);
                }}
              />

              {rangePreview && (
                <Typography variant="body2" color="text.secondary">
                  Intervallo selezionato:{' '}
                  <strong>
                    {dayjs(rangePreview.startDate).format('DD/MM/YYYY')}{' '}
                    {rangePreview.startTime}
                    {' → '}
                    {dayjs(rangePreview.endDate).format('DD/MM/YYYY')}{' '}
                    {rangePreview.endTime}
                  </strong>
                </Typography>
              )}

              <TextField
                label="Altre informazioni"
                value={form.altreInfo}
                onChange={update('altreInfo')}
                fullWidth
                multiline
                minRows={3}
                placeholder="Attività prevista, esigenze particolari…"
              />

              <FormControlLabel
                sx={{ alignItems: 'center', mr: 0, ml: 0 }}
                control={
                  <Checkbox
                    checked={regolamentoAccepted}
                    onChange={(e) => {
                      setRegolamentoAccepted(e.target.checked);
                      setToast(null);
                    }}
                    required
                  />
                }
                label={
                  <Typography variant="body2" sx={{ lineHeight: 1.45, pt: 0.5, display: 'inline' }}>
                    Inviando la prenotazione dichiaro di aver letto e accettato
                    il{' '}
                    <Link
                      href={REGOLAMENTO_PDF_PATH}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Regolamento della base
                    </Link>
                  </Typography>
                }
              />

              {!canSubmit && !submitting && (
                <Alert severity="warning" sx={{ py: 0.75 }}>
                  Per inviare completa ancora:
                  <Box
                    component="ul"
                    sx={{ m: 0, mt: 0.5, pl: 2.25, mb: 0 }}
                  >
                    {submitBlockers.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </Box>
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting || !canSubmit}
                sx={{
                  mt: 0.5,
                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                  minWidth: { sm: 200 },
                }}
              >
                {submitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Invia richiesta'
                )}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        slotProps={{
          paper: {
            sx: {
              borderRadius: '2',
              border: `1px solid ${colors.outlineVariant}`,
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: colors.primary, pt: 3 }}>
          Uscire dalla prenotazione?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Hai già iniziato a compilare il modulo. Se torni indietro perderai i
            dati inseriti.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Resta qui
          </Button>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              goBack();
            }}
            variant="contained"
            color="error"
          >
            Esci e perdi i dati
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setToast(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 24, sm: 32 } }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            icon={
              toast.severity === 'success' ? (
                <EventAvailableOutlinedIcon />
              ) : undefined
            }
            sx={{
              width: '100%',
              borderRadius: '16px',
              alignItems: 'center',
              boxShadow: `0 8px 24px ${colors.primary}33`,
            }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </AppLayout>
  );
}
